import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FilePdf, ListBullets, ClipboardText, Info } from "@phosphor-icons/react";

/**
 * Kleines Panel zum Herunterladen der Rollkarte für einen Tag.
 * Zwei Modi: nur Adressen (kompakt für Klemmbrett) oder mit Fahrplan (mit Notizen, HUB, Zeitfenster).
 */
export default function RollcardPanel() {
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState("addresses");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const download = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.get(`/tours/${dateStr}/rollcard.pdf`, {
        params: { mode },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rollkarte-${dateStr}-${mode}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
      setMsg({ ok: true, msg: "Download gestartet" });
    } catch (e) {
      const detail = e.response?.status === 404 ? "Keine Jobs an diesem Tag" : (e.response?.data?.detail || "Fehler");
      setMsg({ ok: false, msg: detail });
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-white border border-black/10 p-5 rounded-lg" data-testid="rollcard-panel">
      <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
        <ClipboardText size={18} weight="bold" /> Rollkarte drucken (PDF)
      </h3>
      <p className="text-xs text-zinc-500 mb-4">
        Kompakter Ausdruck für den Fahrer — alle Stopps eines Tages, wahlweise nur Adressen oder mit vollem Fahrplan (Zeitfenster, HUB, Notizen).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-3 items-end">
        <div>
          <Label className="text-xs">Tour-Datum</Label>
          <Input type="date" className="h-10 rounded-none" value={dateStr} onChange={(e) => setDateStr(e.target.value)} data-testid="rollcard-date-input" />
        </div>
        <div>
          <Label className="text-xs">Modus</Label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode("addresses")}
              className={`flex-1 h-10 rounded-none text-sm font-medium border flex items-center justify-center gap-2 ${mode === "addresses" ? "bg-black text-white border-black" : "bg-white border-black/10 text-zinc-700 hover:bg-zinc-50"}`}
              data-testid="rollcard-mode-addresses"
            >
              <ListBullets size={14} weight="bold" /> Nur Adressen
            </button>
            <button
              type="button"
              onClick={() => setMode("schedule")}
              className={`flex-1 h-10 rounded-none text-sm font-medium border flex items-center justify-center gap-2 ${mode === "schedule" ? "bg-black text-white border-black" : "bg-white border-black/10 text-zinc-700 hover:bg-zinc-50"}`}
              data-testid="rollcard-mode-schedule"
            >
              <ClipboardText size={14} weight="bold" /> Mit Fahrplan
            </button>
          </div>
        </div>
        <Button onClick={download} disabled={busy || !dateStr} className="rounded-none bg-red-600 text-white hover:bg-red-700 h-10" data-testid="rollcard-download-btn">
          <FilePdf size={13} weight="bold" className="mr-1.5" /> {busy ? "Erzeuge…" : "PDF erzeugen"}
        </Button>
      </div>
      {msg && (
        <div className={`mt-3 text-xs font-medium flex items-center gap-2 ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>
          {msg.ok ? "✓" : "✗"} {msg.msg}
        </div>
      )}
      <div className="mt-4 flex items-start gap-2 p-3 rounded bg-zinc-50 border border-black/5 text-xs text-zinc-600">
        <Info size={14} weight="fill" className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          Die PDF-Reihenfolge folgt der aktuellen Tour-Optimierung. Wird die Tour vom Fahrer abgeschlossen, wird die Reihenfolge als „gelernte Route" hinterlegt und beim nächsten identischen Wochentag automatisch übernommen.
        </div>
      </div>
    </section>
  );
}
